import { Portal } from "@mantine/core";
import { useEffect, useLayoutEffect, useRef, useState } from "react";
import tooltipClasses from "./keywordTooltip.module.css";
import { ContentToken } from "./types";

export const useTooltipStyles = () => ({ classes: tooltipClasses });

type KeywordToken = Extract<ContentToken, { type: "keyword" }>;

const TOOLTIP_OFFSET_FROM_TERM = 12;
/** Horizontal padding inside `.definitionBoundary` before tooltip center is clamped. */
const TOOLTIP_OFFSET_FROM_PARENT_EDGE = 12;

function getContentTokenBounds(container: HTMLElement): DOMRect {
  const children = Array.from(container.children) as HTMLElement[];
  if (children.length === 0) return container.getBoundingClientRect();

  let minL = Infinity;
  let minT = Infinity;
  let maxR = -Infinity;
  let maxB = -Infinity;

  for (const c of children) {
    const r = c.getBoundingClientRect();
    if (r.width === 0 && r.height === 0) continue;
    minL = Math.min(minL, r.left);
    minT = Math.min(minT, r.top);
    maxR = Math.max(maxR, r.right);
    maxB = Math.max(maxB, r.bottom);
  }

  if (minL === Infinity) return container.getBoundingClientRect();
  return new DOMRect(minL, minT, maxR - minL, maxB - minT);
}

/** Clamping rect for tooltip position: nearest `.definitionBoundary` ancestor of the keyword, else token bounds. */
function getTooltipClampBounds(
  keywordEl: HTMLElement | null,
  container: HTMLElement
): { bounds: DOMRect; horizontalInset: number } {
  const boundary = keywordEl?.closest(
    ".definitionBoundary"
  ) as HTMLElement | null;
  if (boundary) {
    return {
      bounds: boundary.getBoundingClientRect(),
      horizontalInset: TOOLTIP_OFFSET_FROM_PARENT_EDGE,
    };
  }
  return { bounds: getContentTokenBounds(container), horizontalInset: 0 };
}

/** Shrinks the rect from left/right for horizontal clamping; caps inset if the box is narrow. */
function insetHorizontalBounds(bounds: DOMRect, inset: number): DOMRect {
  if (inset <= 0) return bounds;
  const maxInset = Math.max(0, (bounds.width - 1) / 2);
  const i = Math.min(inset, maxInset);
  return new DOMRect(
    bounds.left + i,
    bounds.top,
    bounds.width - 2 * i,
    bounds.height
  );
}

function clampTooltipCenterX(
  keywordCenterX: number,
  tooltipWidth: number,
  bounds: DOMRect
): number {
  const half = tooltipWidth / 2;
  const minCenter = bounds.left + half;
  const maxCenter = bounds.right - half;

  if (minCenter <= maxCenter) {
    return Math.max(minCenter, Math.min(maxCenter, keywordCenterX));
  }
  return (bounds.left + bounds.right) / 2;
}

function computeVerticalLayout(
  kwTop: number,
  kwBottom: number,
  tooltipHeight: number,
  bounds: DOMRect
): { top: number; placement: "above" | "below" } {
  const th = tooltipHeight;
  const aboveTop = kwTop - TOOLTIP_OFFSET_FROM_TERM - th;
  const belowTop = kwBottom + TOOLTIP_OFFSET_FROM_TERM;

  const spaceAbove = kwTop - bounds.top;
  const spaceBelow = bounds.bottom - kwBottom;

  const aboveFits = aboveTop >= bounds.top && aboveTop + th <= bounds.bottom;
  const belowFits = belowTop >= bounds.top && belowTop + th <= bounds.bottom;

  let placement: "above" | "below";
  let top: number;

  if (aboveFits && belowFits) {
    placement = "above";
    top = aboveTop;
  } else if (aboveFits) {
    placement = "above";
    top = aboveTop;
  } else if (belowFits) {
    placement = "below";
    top = belowTop;
  } else {
    placement = spaceAbove >= spaceBelow ? "above" : "below";
    top = placement === "above" ? aboveTop : belowTop;
    const minTop = bounds.top;
    const maxTop = Math.max(bounds.top, bounds.bottom - th);
    top = Math.max(minTop, Math.min(maxTop, top));
  }

  return { top, placement };
}

interface KeywordTooltipProps {
  containerRef: React.RefObject<HTMLDivElement>;
  keywords: KeywordToken[];
  renderDefinition: (definition: ContentToken[]) => React.ReactNode;
}

export default function KeywordTooltip({
  containerRef,
  keywords,
  renderDefinition,
}: KeywordTooltipProps) {
  const { classes } = useTooltipStyles();
  const hoveredKeywordElRef = useRef<HTMLElement | null>(null);
  const pendingKwElRef = useRef<HTMLElement | null>(null);
  const openTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const suppressUntilRef = useRef(0);
  const tooltipRef = useRef<HTMLDivElement | null>(null);
  const [tooltipNode, setTooltipNode] = useState<HTMLDivElement | null>(null);

  const [hoveredKw, setHoveredKw] = useState<{
    token: KeywordToken;
    rect: DOMRect;
  } | null>(null);

  const [layout, setLayout] = useState<{
    left: number;
    top: number;
    placement: "above" | "below";
  } | null>(null);

  /** When set, caps tooltip width to `min(40ch, this value)` so it stays within `.definitionBoundary`. */
  const [boundaryMaxTooltipWidthPx, setBoundaryMaxTooltipWidthPx] = useState<
    number | null
  >(null);

  const hoveredKwRef = useRef(hoveredKw);
  hoveredKwRef.current = hoveredKw;

  const clearOpenTimer = () => {
    if (openTimeoutRef.current) {
      clearTimeout(openTimeoutRef.current);
      openTimeoutRef.current = null;
    }
    pendingKwElRef.current = null;
  };

  const dismissTooltip = () => {
    clearOpenTimer();
    hoveredKeywordElRef.current = null;
    setHoveredKw(null);
  };

  // --- Hover listeners (mouseover/mouseout: mouseenter on container misses
  // child→keyword moves; mouseleave fires when entering a portaled tooltip) ---
  useEffect(() => {
    const container = containerRef.current;
    if (!container) return;

    const handleMouseOver = (e: MouseEvent) => {
      const kwEl = (e.target as HTMLElement).closest(
        "[data-kw-idx]"
      ) as HTMLElement | null;
      if (!kwEl || !container.contains(kwEl)) return;
      if (Date.now() < suppressUntilRef.current) return;

      if (hoveredKeywordElRef.current === kwEl) return;

      if (pendingKwElRef.current === kwEl && openTimeoutRef.current) return;

      clearOpenTimer();
      pendingKwElRef.current = kwEl;
      openTimeoutRef.current = setTimeout(() => {
        openTimeoutRef.current = null;
        pendingKwElRef.current = null;
        if (Date.now() < suppressUntilRef.current) return;
        const raw = kwEl.getAttribute("data-kw-idx");
        const idx = raw != null ? parseInt(raw, 10) : NaN;
        if (Number.isNaN(idx) || !keywords[idx]) return;
        hoveredKeywordElRef.current = kwEl;
        setHoveredKw({
          token: keywords[idx],
          rect: kwEl.getBoundingClientRect(),
        });
      }, 250);
    };

    const handleMouseOut = (e: MouseEvent) => {
      const related = e.relatedTarget as Node | null;
      const tip = tooltipRef.current;

      if (tip && related && tip.contains(related)) return;

      const fromKw = (e.target as HTMLElement).closest(
        "[data-kw-idx]"
      ) as HTMLElement | null;

      if (fromKw && related && fromKw.contains(related)) return;

      if (fromKw && related) {
        const toKw = (related as HTMLElement).closest(
          "[data-kw-idx]"
        ) as HTMLElement | null;
        if (toKw && toKw !== fromKw && container.contains(toKw)) {
          clearOpenTimer();
          const raw = toKw.getAttribute("data-kw-idx");
          const idx = raw != null ? parseInt(raw, 10) : NaN;
          if (Number.isNaN(idx) || !keywords[idx]) return;
          hoveredKeywordElRef.current = toKw;
          setHoveredKw({
            token: keywords[idx],
            rect: toKw.getBoundingClientRect(),
          });
          return;
        }
      }

      if (!fromKw) {
        if (
          hoveredKwRef.current &&
          (!related || !container.contains(related)) &&
          !(tip && related && tip.contains(related))
        ) {
          dismissTooltip();
        }
        return;
      }

      if (related && container.contains(related)) {
        const toKw = (related as HTMLElement).closest(
          "[data-kw-idx]"
        ) as HTMLElement | null;
        if (!toKw || toKw !== fromKw) {
          dismissTooltip();
        }
      } else if (!related || !container.contains(related)) {
        dismissTooltip();
      }
    };

    container.addEventListener("mouseover", handleMouseOver);
    container.addEventListener("mouseout", handleMouseOut);

    return () => {
      container.removeEventListener("mouseover", handleMouseOver);
      container.removeEventListener("mouseout", handleMouseOut);
      clearOpenTimer();
    };
  }, [containerRef, keywords]);

  // --- Leaving portaled tooltip (container.contains is too broad: plain text still "inside") ---
  useEffect(() => {
    const tip = tooltipNode;
    if (!tip || !hoveredKw) return;

    const handleTooltipLeave = (e: MouseEvent) => {
      const to = e.relatedTarget as Node | null;
      if (to && tip.contains(to)) return;
      const kw = hoveredKeywordElRef.current;
      if (kw && to && (kw === to || kw.contains(to))) return;
      clearOpenTimer();
      suppressUntilRef.current = Date.now() + 150;
      hoveredKeywordElRef.current = null;
      setHoveredKw(null);
    };

    tip.addEventListener("mouseleave", handleTooltipLeave);
    return () => tip.removeEventListener("mouseleave", handleTooltipLeave);
  }, [tooltipNode, hoveredKw]);

  // --- Dismiss on scroll / resize ---
  useEffect(() => {
    if (!hoveredKw) return;

    const el = hoveredKeywordElRef.current;
    if (!el) return;

    const dismiss = () => {
      clearOpenTimer();
      suppressUntilRef.current = Date.now() + 400;
      hoveredKeywordElRef.current = null;
      setHoveredKw(null);
    };

    const handleScroll = (e: Event) => {
      const node = e.target as Node | null;
      const tip = tooltipRef.current;
      if (node && tip && (tip === node || tip.contains(node))) return;
      dismiss();
    };

    const scrollParents: (HTMLElement | Window)[] = [window];
    let node: HTMLElement | null = el;
    while (node && node !== document.documentElement) {
      const { overflowY, overflowX } = getComputedStyle(node);
      if (
        /(auto|scroll|overlay)/.test(overflowY) ||
        /(auto|scroll|overlay)/.test(overflowX)
      ) {
        scrollParents.push(node);
      }
      node = node.parentElement;
    }

    for (const p of scrollParents) {
      p.addEventListener("scroll", handleScroll, true);
    }
    window.addEventListener("resize", dismiss);

    return () => {
      for (const p of scrollParents) {
        p.removeEventListener("scroll", handleScroll, true);
      }
      window.removeEventListener("resize", dismiss);
    };
  }, [hoveredKw?.token]);

  // --- Position clamping ---
  const keywordCenterX = hoveredKw
    ? hoveredKw.rect.left + hoveredKw.rect.width / 2
    : 0;

  const arrowOffsetX =
    hoveredKw && layout != null ? keywordCenterX - layout.left : 0;

  const setTooltipRefCb = (node: HTMLDivElement | null) => {
    tooltipRef.current = node;
    setTooltipNode(node);
  };

  useLayoutEffect(() => {
    if (!hoveredKw) {
      setLayout(null);
      setBoundaryMaxTooltipWidthPx(null);
      return;
    }

    const container = containerRef.current;
    const tip = tooltipNode;
    if (!container || !tip) {
      setLayout({
        left: keywordCenterX,
        top: hoveredKw.rect.top - TOOLTIP_OFFSET_FROM_TERM - 96,
        placement: "above",
      });
      return;
    }

    const update = () => {
      const c = containerRef.current;
      const t = tooltipRef.current;
      if (!c || !t || !hoveredKw) return;

      const { bounds, horizontalInset } = getTooltipClampBounds(
        hoveredKeywordElRef.current,
        c
      );
      const hClampBounds = insetHorizontalBounds(bounds, horizontalInset);
      const tr = t.getBoundingClientRect();
      const kwCx = hoveredKw.rect.left + hoveredKw.rect.width / 2;

      const left = clampTooltipCenterX(kwCx, tr.width, hClampBounds);
      const { top, placement } = computeVerticalLayout(
        hoveredKw.rect.top,
        hoveredKw.rect.bottom,
        tr.height,
        bounds
      );

      setBoundaryMaxTooltipWidthPx(
        horizontalInset > 0
          ? Math.max(0, bounds.width - 2 * horizontalInset)
          : null
      );
      setLayout({ left, top, placement });
    };

    update();

    const ro = new ResizeObserver(update);
    ro.observe(tip);
    return () => ro.disconnect();
  }, [hoveredKw, keywordCenterX, tooltipNode, containerRef]);

  if (!hoveredKw) return null;

  const isBelow = layout?.placement === "below";

  return (
    <Portal>
      <div
        ref={setTooltipRefCb}
        className={classes.tooltip}
        style={{
          position: "fixed",
          top:
            layout?.top ?? hoveredKw.rect.top - TOOLTIP_OFFSET_FROM_TERM - 96,
          left: layout?.left ?? keywordCenterX,
          transform: "translateX(-50%)",
          zIndex: 1000,
          pointerEvents: "auto",
          ...(boundaryMaxTooltipWidthPx != null
            ? {
                maxWidth: `min(40ch, ${boundaryMaxTooltipWidthPx}px)`,
              }
            : {}),
        }}
      >
        {renderDefinition(hoveredKw.token.definition)}
        <div
          className={classes.arrow}
          style={{
            position: "absolute",
            ...(isBelow
              ? { top: -5, bottom: "auto" }
              : { bottom: -5, top: "auto" }),
            left: `calc(50% + ${arrowOffsetX}px)`,
            transform: isBelow
              ? "translateX(-50%) rotate(-45deg) scaleY(-1)"
              : "translateX(-50%) rotate(45deg)",
            width: 10,
            height: 10,
          }}
        />
      </div>
    </Portal>
  );
}

